import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { EditorButtonConfiguration, IoniconsOutline, NavigationData } from '@pt/pt-models';
import { WidgetUtils } from '../../widgets/widget.utils';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { of } from 'rxjs';
import { delay } from 'rxjs/operators';

@Component({
  selector: 'pt-editor-sidebar-navigation-data',
  template: `
    <div class='form-group editor-sidebar-text mb-2'>
      <label [htmlFor]='id' [innerHTML]='label'></label>
      <div class='action'>
        <pt-widget-action
          actionStyle='TEXT'
          [showCancel]='editorButton?.showCancel'
          [showEdit]='editorButton?.showEdit'
          [showLoader]='false'
          [showReload]='false'
          [showSave]='editorButton?.showSave'
          [showIcon]='false'

          [disabledEdit]='editorButton?.disabledEdit'
          [disabledSave]='editorButton?.disabledSave'
          [disabledCancel]='editorButton?.disabledCancel'

          (clickEdit)='clickEdit.emit(); showPopup(popover)'
          (clickCancel)='clickCancel.emit()'
          (clickSave)='clickSave.emit(currentData?.id)'
        ></pt-widget-action>
      </div>

      <ng-template #fieldPopContent class='p-0'>
        <pt-resource-navigation
          [title]='dataLabel'
          [data]='data'
          [selectedResourceId]='currentId'
          (clickClose)='popover.close()'
          (clickItem)='selectData($event)'>
        </pt-resource-navigation>
      </ng-template>

      <ng-container *ngIf="editorButton.showSave">
        <input
          type='text'
          autocomplete='off'
          class='form-control form-control-sm'
          [readOnly]='true'
          [ngbPopover]='fieldPopContent'
          [autoClose]="'outside'"
          #popover='ngbPopover'
          triggers='manual'
          placement='left'
          container="body"
          (click)='popover.open()'
          [(ngModel)]='currentLabel'
          [ngClass]="{'text-black': !editorButton?.disabledSave}"
          [id]='id'>
        <div *ngIf='!currentLabel' class='missing-value' (click)='editorButton.showSave ? popover.open() : null'>N/A</div>
      </ng-container>

      <ng-container *ngIf="!editorButton.showSave" >
        <div *ngIf="link" class=" sidebar-readonly sidebar-link text-truncate"
             (click)='toLink(link)'>
          {{currentLabel || 'N/A'}}
        </div>
        <div *ngIf="!link" class=" sidebar-readonly text-truncate" (click)='editorButton.showSave ? popover.open() : null'>
          {{currentLabel || 'N/A'}}
        </div>
      </ng-container>



      <pt-icon *ngIf='optional && editorButton.showSave && currentLabel'
               [key]='IconClose'
               [size]='14'
               class='clean-value'
               (click)='cleanValue()'>
      </pt-icon>
    </div>
  `,
  styles: [
    `
      .sidebar-readonly {
        border-bottom: 1px solid #33333333;
        font-weight: bold;
        display: flex;
        align-items: flex-end;
        height: 22px;
        width: 100%;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .sidebar-link {
        color: #0d6efd;
        text-decoration: underline;
      }
      .sidebar-link:hover {
        cursor: pointer;
        background: transparent;
      }

      .missing-value {
        position: absolute;
        left: 2px;
        bottom: 0;
        opacity: .7;
      }

      .clean-value {
        position: absolute;
        right: 0;
        bottom: 0;
        opacity: .7;
        cursor: pointer;
      }

      .clean-value:hover {
        opacity: 1;
      }

      .editor-sidebar-text {
        position: relative;
        margin-bottom: 0;
      }

      .editor-sidebar-text .text-black {
        color: #495057 !important;
        opacity: 1 !important;
      }

      .editor-sidebar-text .action {
        float: right;
        margin-top: 3px;
      }

      .editor-sidebar-text label {
        margin-bottom: 2px;
        margin-top: 5px;
      }

      .editor-sidebar-text input {
        height: 25px;
        padding: 0;
        line-height: 1;
        border-radius: 0;
        border-top: 0;
        border-left: 0;
        border-right: 0;
        font-weight: bold;
        color: #000000;
      }

      .editor-sidebar-text .form-control:disabled, .form-control[readonly] {
        background-color: #ffffff !important;
        opacity: .7;
      }

      .editor-sidebar-text .form-control:disabled, .form-control[readonly]:focus {
        border-color: #dfdfdf;
        box-shadow: none;
      }
    `
  ]
})
export class EditorSidebarNavigationDataComponent implements OnChanges {
  @Input() label: string;
  @Input() value: number;
  @Input() editorButton: EditorButtonConfiguration;
  @Input() link?: string;

  @Input() data: NavigationData[];
  @Input() dataLabel: string;

  @Input() optional: boolean;

  @ViewChild('popover') popover;

  currentData: NavigationData;
  currentId: number;
  currentLabel: string;

  @Output() clickEdit = new EventEmitter();
  @Output() clickSave = new EventEmitter<number>();
  @Output() clickCancel = new EventEmitter();

  id: string;

  IconClose = IoniconsOutline.close;

  constructor() {
    this.id = WidgetUtils.generateId('esnd_');
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.value || changes.data) {
      this.currentData = this.data?.find((d: NavigationData) => d.id === this.value);
      this.currentId = this.currentData?.id;
      this.currentLabel = this.currentData?.name;
    }
  }

  selectData(selectedData: NavigationData) {
    this.currentData = selectedData;
    this.currentId = this.currentData?.id;
    this.currentLabel = this.currentData?.name;
    this.popover.close();
  }

  cleanValue() {
    this.currentData = null;
    this.currentId = null;
    this.currentLabel = null;
  }

  showPopup(popover: NgbPopover) {
    return of(true).pipe(
      delay(200)
    ).subscribe(() => {
      if (this.editorButton?.showSave && popover) {
        return popover.open();
      }
    });
  }

  toLink(link: string) {
    if (link) {
      window.open(link, '_blank');
    }

  }
}
